import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from './rn';
import { fontSize, radius, spacing } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';

export type LiveShareState = {
  id: number;
  status: 'active' | 'stopped' | 'expired';
  expires_at: string | null;
  last_point_at: string | null;
};

/** Kart içeriği sunucuda yazılıyor; bozuksa satır çizilmez. */
export function parseLiveShareCard(content: string): { shareId: number } | null {
  try {
    const d = JSON.parse(content);
    const id = Number(d?.shareId);
    return Number.isInteger(id) && id > 0 ? { shareId: id } : null;
  } catch {
    return null;
  }
}

/**
 * Paylaşımın hâlâ açık olup olmadığı; süresi geçmişse açık sayılmıyor.
 *
 * Sunucu da aynı soruyu soruyor (`locationShareReadable`), ama kart bekleyemez:
 * süpürme gelene kadar kapanmış bir paylaşımı açık göstermek, izleyene
 * olmayan bir izin duygusu verirdi.
 */
export function liveShareOpen(state: LiveShareState | null, now: Date): boolean {
  if (!state || state.status !== 'active') return false;
  if (!state.expires_at) return true;
  const bitis = new Date(state.expires_at).getTime();
  return Number.isFinite(bitis) && bitis > now.getTime();
}

type Props = {
  state: LiveShareState | null;
  mine: boolean;
  onOpen: (shareId: number) => void;
  shareId: number;
  now?: Date;
};

/**
 * Sohbetteki canlı paylaşım satırı (9.3.3.5 · D-584).
 *
 * Karşı taraf paylaşımdan başka türlü haberdar olamazdı — ve izlendiğini
 * bilmeyen kimse izlemeye başlayamaz. Kart yalnız haber vermiyor, izleyenin
 * TEK giriş kapısı: konumu açmak, "bakıyorum" demenin kendisidir (9.3.3.4).
 *
 * Harita kartın içinde ÇİZİLMİYOR: listede canlı bir harita tutmak, tam da
 * kaçındığımız sürekli ölçümü geri getirirdi.
 */
export default function MessageLiveLocation({ state, mine, onOpen, shareId, now }: Props) {
  const colors = useTheme();
  const { t } = useTranslation();
  const acik = liveShareOpen(state ?? null, now ?? new Date());
  const renk = mine ? colors.white : colors.black;

  return (
    <TouchableOpacity
      testID="message-live-location"
      activeOpacity={0.9}
      disabled={!acik || mine}
      onPress={() => onOpen(shareId)}>
      <View style={styles.card}>
        <Text style={[styles.title, { color: renk }]}>{t('message.liveLocationCardTitle')}</Text>
        <Text testID="live-share-status" style={[styles.status, { color: renk }]}>
          {t(acik ? 'message.liveLocationCardActive' : 'message.liveLocationCardEnded')}
        </Text>
        {/* Giriş yalnız izleyende ve yalnız paylaşım açıkken çizilir: basılınca
            hiçbir şey yapmayan bir satır, olmayan bir satırdan kötüdür. */}
        {acik && !mine && (
          <Text testID="live-share-open" style={[styles.open, { color: renk }]}>
            {t('message.liveLocationOpen')}
          </Text>
        )}
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: { minWidth: 170, gap: spacing.xs, borderRadius: radius.card },
  title: { fontSize: fontSize.sm, opacity: 0.7 },
  status: { fontSize: fontSize.md, fontWeight: '600' },
  open: { fontSize: fontSize.sm, fontWeight: '700', marginTop: spacing.xs },
});
